import { toast } from 'sonner';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { useSetFeatureFlagMutation } from '@reflex/ui/client/api/feature-flags';
import type { OrgId } from '@reflex/ui/client/api/org-id';

/**
 * Toggle a global feature flag. Wraps the org-safe generated facade
 * (`useSetFeatureFlagMutation`, which invalidates the admin and session-scoped
 * flag lists) and keeps the page's `{ key, enabled }` call shape plus the
 * success/error toasts. `org` is required at compile time; see
 * `useActiveOrgId`.
 */
export function useSetFlagMutation(org: OrgId) {
  const mutation = useSetFeatureFlagMutation(org);
  return {
    isPending: mutation.isPending,
    mutate: ({ key, enabled }: { key: string; enabled: boolean }) =>
      mutation.mutate(
        { key, data: { enabled } },
        {
          onSuccess: () => toast.success('Flag updated'),
          onError: (err: unknown) =>
            toast.error('Failed to update flag', {
              description: toErrorMessage(err),
            }),
        },
      ),
  };
}
